/**
 * useShareChat.ts - Create / revoke a public share link for the current chat.
 *
 * Talks to the dedicated, isolated share endpoints (POST/DELETE /ua/chat/share)
 * via the authenticated API client. Builds the final link from the live page
 * origin (proxy-safe) so it works regardless of how the host is detected.
 */

import { useState } from 'react'
import { useApi } from 'core'

export const useShareChat = () => {
    const api = useApi()
    const [loading, setLoading] = useState(false)
    const [shareUrl, setShareUrl] = useState<string | null>(null)
    const [shareToken, setShareToken] = useState<string | null>(null)

    const createShare = async (postId: string, circleId?: string): Promise<string> => {
        setLoading(true)
        try {
            const res = await api.post({ path: 'chat/share', body: { postId, circleId } })
            const path = res.sharePath || `/ua/chat/share/${res.shareToken}`
            const url = `${window.location.origin}${path}`
            setShareToken(res.shareToken)
            setShareUrl(url)
            return url
        } finally {
            setLoading(false)
        }
    }

    const revokeShare = async (): Promise<void> => {
        if (!shareToken) return
        await api.delete({ path: `chat/share/${shareToken}` })
        setShareToken(null)
        setShareUrl(null)
    }

    const reset = () => {
        setShareUrl(null)
        setShareToken(null)
    }

    return { loading, shareUrl, shareToken, createShare, revokeShare, reset }
}
